'use client';

import { useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

// Style
import { useTranslations } from 'next-intl';
import { Box, Button, Flex, Skeleton, Text } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { IRedux } from '@/_store/types';
import { useRouter } from '@/i18n/navigation';
import { EXCLUDE_STOP } from '@/_store/cart/cart.slice';
import NumberToPrice from '@/_components/NumberToPrice';
import productService from '@/_service/product.service';
import Auth from '@/_components/Auth';
import { syncCartStopFlagsFromMenuProducts } from '@/_utils/cartMenuAvailability';
import getShipperById from '@/_service/shipper.service';
import { RightIcon } from '@/_assets/icon-components';
import { IActivePoint } from '../../_checkout/_hooks/useDelivery';
import Image from 'next/image';
import personIcon from '@/shared/assets/svg/person.svg';
import useDelivery from '../_hooks/useDelivery';
import { getBrandHref } from '@/_lib/brand-href';

interface BillProps {
  selectedShipperId: string;
  totalPrice: number;
}

export default function Bill({ totalPrice, selectedShipperId }: BillProps) {
  const [isAuthDialog, setIsAuthDialog] = useState(false);
  const [activePoint, setActivePoint] = useState<IActivePoint | null>(null);

  const { deliveryPrice, loading } = useDelivery(totalPrice, selectedShipperId);

  const { user, cartState, common, settings, session } = useSelector(
    (state: IRedux) => ({
      user: state.auth.user,
      cartState: state.cart?.cart,
      common: state.common,
      settings: state.settings.data,
      session: state.session,
    }),
    shallowEqual
  );
  const commonState = common;
  const { is_hall, menu_id, order_type } = session;

  const router = useRouter();
  const t = useTranslations();
  const dispatch = useDispatch();

  const shipperId = selectedShipperId;
  const branch = commonState?.branches?.[shipperId];
  const cart = cartState?.[shipperId]?.products || [];

  const productIds = useMemo(
    () => String(cart?.map((product) => product.product_id)),
    [cart]
  );
  const productIdQuantity = useMemo(() => {
    const result: { [key: string]: number } = {};

    cart.forEach((product) => {
      result[product?.product_id] = Number(product.quantity);
    });

    return result;
  }, [cart]);

  const onButtonClick = () => {
    if (!user) {
      setIsAuthDialog(true);
    } else {
      router.push(getBrandHref(shipperId, { path: '/checkout' }));
    }
  };

  const { data: menuProducts } = useQuery({
    queryKey: ['menu-products', branch?.menu_id, productIds, selectedShipperId],
    queryFn: () =>
      productService
        .getMenuProducts({
          menu_id:
            is_hall === 'true'
              ? menu_id !== 'null'
                ? menu_id
                : undefined
              : branch?.menu_id || '',
          items: JSON.stringify(productIdQuantity),
        }, selectedShipperId)
        .then((res) => res?.data),
    enabled: Boolean(
      cart?.length > 0 &&
      (is_hall === 'true'
        ? menu_id && menu_id !== 'null'
        : !!branch?.menu_id)
    ),
  });

  const { data: shipper } = useQuery({
    queryKey: ['GET_SHIPPER_BY_ID', settings],
    queryFn: () =>
      getShipperById(settings?.shipper_id || '').then((res) => res?.data),
    enabled: !!settings?.shipper_id,
  });

  const deliveryZone = commonState?.deliveryZones?.[shipperId];
  const minimalPrice = useMemo(() => {
    if (deliveryZone?.id) return deliveryZone?.min_order_amount || 0;

    return shipper?.minimal_order_price || 0;
  }, [shipper?.minimal_order_price, deliveryZone?.id]);

  // Set delivery details & Get delivery price
  useEffect(() => {
    if (order_type?.type == 'delivery') {
      if (common?.points.length > 0) {
        for (const point of common?.points) {
          if (
            point.isActive &&
            point.location?.[0] !== activePoint?.lat &&
            point.location?.[1] !== activePoint?.long
          ) {
            const newActivePoint = {
              address: point?.address,
              lat: point?.location?.[0] || 0,
              long: point?.location?.[1] || 0,
            };
            if (
              newActivePoint.lat !== activePoint?.lat ||
              newActivePoint.long !== activePoint?.long
            ) {
              setActivePoint(newActivePoint);
            }
            break;
          }
        }
      } else {
        setActivePoint(null);
      }
    }
  }, [common?.points, order_type?.type]);


  useEffect(() => {
    if (!menuProducts || !cart?.length) return;
    syncCartStopFlagsFromMenuProducts(dispatch, cart, menuProducts, {
      shipper_id: shipperId,
      at: new Date(),
    });
  }, [menuProducts, dispatch, shipperId]);

  // Remove product when menu is not found
  useEffect(() => {
    if (!branch?.menu_id && cart) {
      // Clear stopped items from all brands
      dispatch(EXCLUDE_STOP({ shipper_id: shipperId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branch?.menu_id]);

  if (cart.length > 0)
    return (
      <>
        <Box
          position="fixed"
          bottom={0}
          left={0}
          right={0}
          w="full"
          zIndex={2}
          bg="background.card"
          boxShadow="0px -2px 10px rgba(0, 0, 0, 0.08)"
        >
          {/* Delivery Info Bar */}
          <Flex
            px={4}
            py={3}
            alignItems="center"
            justifyContent="space-between"
            borderBottom="1px solid"
            borderColor="gray.100"
            cursor="pointer"
            onClick={() => router.push('/delivery-terms')}
          >
            <Box>
              <Image src={personIcon} alt="delivery" width={24} height={24} />
            </Box>
            {loading ? <Skeleton width="100px" height="20px" /> :
              <Flex alignItems="center" gap={1}>
                <Text fontSize="sm" color="text">
                  {t('delivery')}
                </Text>
                <NumberToPrice
                  value={deliveryPrice || 0}
                  textProps={{
                    fontSize: 'sm',
                    fontWeight: 500,
                  }}
                  spanProps={{
                    fontSize: 'sm',
                    fontWeight: 500,
                  }}
                />
              </Flex>}
            <Box
              w="20px"
              h="20px"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <RightIcon width="12" height="12" />
            </Box>
          </Flex>

          {/* Minimum Price Warning */}
          {Boolean(minimalPrice && minimalPrice > totalPrice) && (
            <Flex px={4} py={2} gap={1} color="red" fontSize={14} justifyContent="center">
              {t('minimum_order_amount')}
              <NumberToPrice value={minimalPrice} />
            </Flex>
          )}

          {/* CTA Button */}
          <Box px={4} pb={4} pt={2}>
            <Button
              onClick={onButtonClick}
              w="full"
              size="xl"
              h="52px"
              bg="primary"
              color="primary.text"
              _hover={{ bg: 'primary.hover' }}
              rounded={16}
              disabled={Boolean(minimalPrice && minimalPrice > totalPrice)}
            >
              {t('next')}
            </Button>
          </Box>
        </Box>
        <Auth
          open={isAuthDialog}
          onClose={() => setIsAuthDialog(false)}
          onLastConfirm={() =>
            router.push(getBrandHref(shipperId, { path: '/checkout' }))
          }
        />
      </>
    );

  return null;
}
